import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RuntimeConfig } from '@umijs/max';
import { history } from '@umijs/max';
// NOTE: the dev-only ResizeObserver-loop error suppressor lives in
// config/config.ts headScripts — it must register BEFORE the bundle runs,
// because Mako's HMR runtime adds its own window error listener first and
// listeners fire in registration order (a later listener can't preempt it).
import {
  ActingAsMerchantBanner,
  AvatarDropdown,
  AvatarName,
  BackToMerchantListMenuItem,
  Footer,
  MerchantInitializer,
  PartnerManagementBanner,
  SelectLang,
} from '@/components';
import { getActingAsMerchantOwner } from '@/models/actingAsMerchant';
import {
  clearEnteredMerchantId,
  getEnteredMerchantId,
} from '@/models/merchantWorkspace';
import ForbiddenPage from '@/pages/403';
import { currentUser as queryCurrentUser } from '@/services/auth/api';
import AntdStaticBridge from '@/utils/antdStatic';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const merchantsPath = '/merchants';

// Push to login carrying the current location, so after signing back in the
// user returns to where they were (login page consumes ?redirect= safely).
const pushToLogin = () => {
  const { pathname, search } = history.location;
  if (pathname === loginPath) return;
  history.push(
    `${loginPath}?redirect=${encodeURIComponent(pathname + search)}`,
  );
};

// Pages that don't require authentication
const authWhitelist = [
  '/user/login',
  '/user/register',
  '/user/register-result',
  '/user/reset-password',
  '/accept-invitation',
];

// Pages accessible without merchants (for new users)
const noMerchantWhitelist = [
  '/merchants',
  '/account/settings',
  '/account/staff-management',
  '/onboarding/callback', // Keep callback for Adyen redirect
  '/admin', // SuperAdmin routes don't need merchants
];

// Pages excluded from the "must enter a merchant workspace first" guard —
// the merchant list/detail flow itself, plus utility routes that aren't
// merchant-scoped workspace pages.
const workspaceGuardExcluded = [
  '/merchants',
  '/account',
  '/admin',
  '/onboarding/callback',
  '/user',
];

// Helper to check if user has merchants using user-specific key
const checkUserHasMerchants = (userId?: string): boolean => {
  if (!userId) return false;
  const merchantsKey = `aimerpay_merchants_${userId}`;
  const merchantsJson = localStorage.getItem(merchantsKey);
  if (merchantsJson) {
    try {
      const merchants = JSON.parse(merchantsJson);
      return Array.isArray(merchants) && merchants.length > 0;
    } catch {
      return false;
    }
  }
  return false;
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    // Check if we have a token
    const token = localStorage.getItem('aimerpay_token');
    if (!token) {
      const currentPath = history.location.pathname;
      if (!authWhitelist.includes(currentPath)) {
        pushToLogin();
      }
      return undefined;
    }

    try {
      const result = await queryCurrentUser({
        skipErrorHandler: true,
      });
      if (result.success && result.data) {
        // Map user data for compatibility
        return {
          ...result.data,
          name: result.data.displayName || result.data.username,
          userid: result.data.id,
          access: result.data.role,
        };
      }
      // Token invalid, clear and redirect
      localStorage.removeItem('aimerpay_token');
      localStorage.removeItem('aimerpay_refresh_token');
      localStorage.removeItem('aimerpay_token_expires');
      const currentPath = history.location.pathname;
      if (!authWhitelist.includes(currentPath)) {
        pushToLogin();
      }
    } catch (_error) {
      // Clear tokens on error
      localStorage.removeItem('aimerpay_token');
      localStorage.removeItem('aimerpay_refresh_token');
      localStorage.removeItem('aimerpay_token_expires');
      // Only redirect to login if not already on auth pages
      const currentPath = history.location.pathname;
      if (!authWhitelist.includes(currentPath)) {
        pushToLogin();
      }
    }
    return undefined;
  };

  const { pathname } = history.location;

  // Skip fetching user info for auth pages (login, register, reset-password)
  // But keep fetchUserInfo available for use after successful login
  if (authWhitelist.includes(pathname)) {
    return {
      fetchUserInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  const currentUser = await fetchUserInfo();
  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout API: https://procomponents.ant.design/components/layout
export const layout: RuntimeConfig['layout'] = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [<SelectLang key="SelectLang" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_: any, avatarChildren: React.ReactNode) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // Watermark disabled
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { pathname } = history.location;

      // Check authentication
      if (!initialState?.currentUser && !authWhitelist.includes(pathname)) {
        pushToLogin();
        return;
      }

      // SuperAdmin doesn't need merchants - skip merchant check
      const userRole =
        initialState?.currentUser?.role || initialState?.currentUser?.access;
      if (userRole === 'SuperAdmin') {
        // If SuperAdmin is in merchant mode but navigated back to admin pages
        // (e.g. via browser back button), auto-exit merchant mode
        if (pathname.startsWith('/admin') && getActingAsMerchantOwner()) {
          const superAdminUserId =
            initialState?.currentUser?.id || initialState?.currentUser?.userid;
          sessionStorage.removeItem('aimerpay_acting_as_merchant_owner');
          sessionStorage.removeItem('aimerpay_acting_as_merchants_cache');
          clearEnteredMerchantId(superAdminUserId);
          setInitialState((prev: any) => ({ ...prev }));
          return;
        }

        if (!getActingAsMerchantOwner()) {
          // SuperAdmin not in merchant mode — redirect away from merchant pages
          if (
            !pathname.startsWith('/admin') &&
            !pathname.startsWith('/account') &&
            !pathname.startsWith('/user')
          ) {
            history.push('/admin/merchant-owners');
          }
          return;
        }

        // SuperAdmin IS in merchant mode: fall through to the shared
        // workspace-entry guard below, same as Admin in merchant mode.
      }

      // Admin (Role=1) in acting-as-owner mode needs no /merchants guard:
      // inside the mode /merchants is the owner's partner grid (sidebar entry),
      // and the owner browse list is reached by exiting via the banner button.

      // Check if user has merchants (for new users without merchants)
      // Skip this check for auth pages and no-merchant whitelist pages
      if (
        initialState?.currentUser &&
        !authWhitelist.includes(pathname) &&
        !noMerchantWhitelist.some((p) => pathname.startsWith(p))
      ) {
        const userId =
          initialState.currentUser.id || initialState.currentUser.userid;
        const hasMerchants = checkUserHasMerchants(userId);

        // If user has no merchants, redirect to merchants page (shows empty state)
        if (!hasMerchants) {
          const role =
            initialState.currentUser.role || initialState.currentUser.access;
          if (
            role === 'MerchantOwner' ||
            role === 'Admin' ||
            getActingAsMerchantOwner()
          ) {
            // Owner sees the empty state; Admin sees the partner browse list.
            // Acting-as belongs here too: /account/settings is gated on
            // canViewSettings (= !inMerchantMode), so sending an impersonating
            // SuperAdmin there is a guaranteed 403 dead-end. /merchants is
            // always reachable in this mode (canViewMerchantList).
            history.push(merchantsPath);
          } else {
            // Other non-owners without merchants go to settings
            history.push('/account/settings');
          }
          return;
        }
      }

      // Workspace-entry guard: once a user (or an Admin/SuperAdmin acting as
      // an owner) has merchants, they must explicitly "Enter" one from the
      // merchant list before reaching any merchant-scoped workspace route.
      // Excludes the merchant list/detail flow itself plus account/admin/
      // auth/callback utility routes — any future workspace route is
      // automatically covered without editing this list.
      if (
        initialState?.currentUser &&
        !authWhitelist.includes(pathname) &&
        !workspaceGuardExcluded.some((p) => pathname.startsWith(p))
      ) {
        const userId =
          initialState.currentUser.id || initialState.currentUser.userid;
        const userHasMerchants = checkUserHasMerchants(userId);
        if (userHasMerchants && getEnteredMerchantId(userId) === null) {
          history.push('/merchants/list');
        }
      }
    },
    menuHeaderRender: undefined,
    // Custom 403 for routes whose access check fails (replaces the layout
    // plugin's built-in hardcoded-Chinese fallback).
    unAccessible: <ForbiddenPage />,
    // The sidebar IS the merchant-workspace navigation, so it only renders
    // inside an entered workspace, and only on workspace routes. Everything
    // else — the merchant list/detail flow, user-level /account pages — has
    // no sidebar. Exception: SuperAdmin outside merchant mode keeps the
    // admin sidebar untouched.
    menuRender: (_props, defaultDom) => {
      const role =
        initialState?.currentUser?.role || initialState?.currentUser?.access;
      if (role === 'SuperAdmin' && !getActingAsMerchantOwner()) {
        return defaultDom;
      }

      const currentPathname = history.location.pathname;
      const isNonWorkspacePath =
        currentPathname === '/merchants' ||
        currentPathname.startsWith('/merchants/list') ||
        /^\/merchants\/\d+$/.test(currentPathname) ||
        currentPathname.startsWith('/account');
      if (isNonWorkspacePath) return null;

      const userId =
        initialState?.currentUser?.id || initialState?.currentUser?.userid;
      if (getEnteredMerchantId(userId) === null) return null;

      return defaultDom;
    },
    // Pin a "back to merchant list" row above the route-driven menu items
    // whenever a merchant workspace is currently entered.
    menuContentRender: (_props, defaultDom) => (
      <>
        <BackToMerchantListMenuItem />
        {defaultDom}
      </>
    ),
    childrenRender: (children: React.ReactNode) => {
      const role =
        initialState?.currentUser?.role || initialState?.currentUser?.access;
      const isSuperAdmin = role === 'SuperAdmin';
      const isAdmin = role === 'Admin';
      const inMerchantMode =
        (isSuperAdmin || isAdmin) && !!getActingAsMerchantOwner();
      return (
        <>
          <AntdStaticBridge />
          {isSuperAdmin && inMerchantMode && <ActingAsMerchantBanner />}
          {isAdmin && inMerchantMode && <PartnerManagementBanner />}
          {(!isSuperAdmin || inMerchantMode) && <MerchantInitializer />}
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState: any) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request configuration
 * @doc https://umijs.org/docs/max/request
 */
export const request: RequestConfig = {
  baseURL: isDev
    ? ''
    : process.env.REACT_APP_API_URL || 'https://aimerpay-api.aimerhq.com',
  ...errorConfig,
};
